import React from 'react';

import {
  Grid, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { toInteger } from 'lodash';
import {
  Bar, BarChart, Cell, ResponsiveContainer, XAxis,
} from 'recharts';

import { Question } from '../../services/exams/types';
import { QuestionType } from '../../types/common';
import {
  formatQuestionLabel,
  getErrorType,
  getMarksBreakdown,
} from '../../utils/common/exams';
import Button from '../Button';
import MarksSelect from '../MarksSelect';
import MultipleChoiceSelect from '../MultipleChoiceSelect';
import SavingStatus from '../SavingStatus';
import Tabs from '../Tabs';

import {
  AnalyticsContainer,
  ButtonContainer,
  Mark,
  MultipleChocieSelectContainer,
  MultipleChoiceContainer,
  MultipleChoiceLabel,
  NavigatorContainer,
  QuestionContainer,
  QuestionLabel,
  QuestionsCard,
  QuestionsContainer,
  SavingStatusContainer,
} from './index.styles';
import {
  ExamAttempt,
  Section,
} from './types';
import { getMarksDataArray, getOptionsDataArray } from './utils';

interface ExamViewerProps {
  heading: string,
  subHeading?: string,
  questionSections: Section[],
  questionSectionIndex: number,
  isSaving?: boolean,
  isSavingError?: boolean,
  isSavingSuccess?: boolean,
  isMCQ: boolean,
  isMarked: boolean,
  MCQCorrect: number,
  MCQTotal: number,
  displayedQuestions: Question[],
  attempts: ExamAttempt,
  showAnalytics?: boolean,
  finishButtonText?: string,
  changeQuestionSection?: (index: number) => void,
  handleMarking?: () => void,
  handleProgress?: () => void,
  handleAttemptChange?: (maxMark: number, questionId: string, attempt: any) => void,
}

const ExamViewer = ({ heading, subHeading,
  questionSections, questionSectionIndex,
  isSaving = false, isSavingError = false,
  isSavingSuccess = false, isMCQ, MCQCorrect, MCQTotal,
  isMarked,
  displayedQuestions,
  attempts,
  showAnalytics = false,
  finishButtonText = 'Submit Exam',
  changeQuestionSection = () => {},
  handleMarking = () => {},
  handleProgress = () => {},
  handleAttemptChange = () => {} }: ExamViewerProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Grid container>
      <Grid item xs={12} md={4}>
        <NavigatorContainer>
          <div>
            <Typography variant="h5">{heading}</Typography>
            {subHeading && <Typography variant="subtitle1">{subHeading}</Typography>}
          </div>
          <Tabs
            options={questionSections.map((section, index) => (
              { key: index.toString(), label: section.sectionLabel }))}
            selectedKey={questionSectionIndex.toString()}
            orientation={isMd ? 'vertical' : 'horizontal'}
            onChange={(key) => {
              if (!isSaving) {
                changeQuestionSection(toInteger(key));
              }
            }}
          />
        </NavigatorContainer>
      </Grid>
      <Grid item xs={12} md={8}>
        <QuestionsCard>
          <SavingStatusContainer>
            <SavingStatus
              isLoading={isSaving}
              isError={isSavingError}
              isSuccess={isSavingSuccess}
            />
          </SavingStatusContainer>
          <QuestionsContainer $isMCQ={isMCQ}>
            {isMCQ && (
              <QuestionLabel variant="h5">
                <Mark>{MCQCorrect} / {MCQTotal}</Mark>
                Multiple Choice Questions
              </QuestionLabel>
            )}
            {
              displayedQuestions.map((question) => {
                if (question.type === QuestionType.MultipleChoice) {
                  return (
                    <MultipleChoiceContainer key={question.questionId}>
                      <MultipleChocieSelectContainer>
                        <MultipleChoiceLabel>
                          {formatQuestionLabel(question.label)}
                        </MultipleChoiceLabel>
                        <MultipleChoiceSelect
                        // mcqOpts only exists for multiple choice questions
                          options={question.mcqOpts?.optionsCount || 0}
                          correctOption={question.mcqOpts?.answer || 0}
                          isMarked={isMarked}
                          onSelect={(option) =>
                            handleAttemptChange(
                              question.maxMark, question.questionId, { response: option })}
                          selectedOption={attempts[question.questionId]?.response}
                          disabled={showAnalytics}
                        />
                      </MultipleChocieSelectContainer>
                      {showAnalytics && (
                        <AnalyticsContainer>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={getOptionsDataArray(question)}>
                              <XAxis
                                type="category"
                                dataKey="name"
                                width={150}
                                tick={{ fontSize: 14, fill: theme.palette.gray.dark }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                                {
                                  getOptionsDataArray(question).map((entry) => (
                                    <Cell
                                      key={entry.name}
                                      fill={entry.correct ?
                                        theme.palette.success.main : theme.palette.backdrop.dark}
                                    />
                                  ))
                                }
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </AnalyticsContainer>
                      )}
                    </MultipleChoiceContainer>
                  );
                }
                return (
                  <QuestionContainer key={question.questionId}>
                    <QuestionLabel variant="h5">
                      <Mark>{attempts[question.questionId]?.mark ?? 0} / {question.maxMark}</Mark>
                      Question {formatQuestionLabel(question.label)}
                    </QuestionLabel>
                    <MarksSelect
                      maxMark={question.maxMark}
                      onChange={(mark, errorType) => handleAttemptChange(
                        question.maxMark, question.questionId, {
                          mark,
                          marksBreakdown: getMarksBreakdown(
                            mark,
                            question.maxMark,
                            errorType,
                          ),
                        })}
                      selectError={(errorType) =>
                        handleAttemptChange(
                          question.maxMark, question.questionId, {
                            marksBreakdown: getMarksBreakdown(
                              attempts[question.questionId]?.mark ?? 0,
                              question.maxMark,
                              errorType,
                            ),
                          })}
                      selectedMark={attempts[question.questionId]?.mark ?? -1}
                      selectedError={getErrorType(attempts[question.questionId]?.marksBreakdown) ?? ''}
                      disabled={showAnalytics}
                    />
                    {showAnalytics && (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={getMarksDataArray(question)}>
                          <XAxis
                            type="category"
                            dataKey="name"
                            width={150}
                            tick={{ fontSize: 14, fill: theme.palette.gray.dark }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Bar dataKey="value" radius={[5, 5, 0, 0]} fill={theme.palette.backdrop.dark} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </QuestionContainer>
                );
              })
            }
          </QuestionsContainer>
          <ButtonContainer>
            {
              isMarked || !isMCQ ? (
                <Button
                  onClick={handleProgress}
                  variant="contained"
                  color={questionSectionIndex === questionSections.length - 1 ? 'success' : 'darkPrimary'}
                  disabled={isSaving}
                >
                  {questionSectionIndex === questionSections.length - 1 ? finishButtonText : 'Next Question'}
                </Button>
              ) :
                (<Button onClick={handleMarking}>Mark</Button>)
            }
          </ButtonContainer>
        </QuestionsCard>
      </Grid>
    </Grid>
  );
};

export default ExamViewer;
